import type { FC } from 'react';
import ScrollableList from '../../../_partials/ScrollableList';
import { STUDIO_DEVIATIONS_STREAM } from '../../../../../../constants';
import { useNormalizedDeviationStream } from '@wix/da-shared-react/pkg/utils/hooks/useStream';
import { useDraftsSelect } from '../../../../../hooks/useDraftsSelect';
import { StudioSection } from '../../../../../types/store';
import CommissionListItem from '../../../items/CommissionListItem';
import OriginalListItem from '../../../items/OriginalListItem';
import ExclusiveListItem from '../../../items/ExclusiveListItem';
import PcpListItem from '../../../items/PcpListItem';
import TierListItem from '../../../items/TierListItem';
import { useSelector } from 'react-redux';
import { getActiveSection, getOriginalsData } from '../../../../../selectors/studio';
import FreeBoostsPromoBanner from '../../../../FreeBoostsPromoBanner';

interface Props {}

const ProductList: FC<Props> = () => {
  const { streamId, streamConfig, streamThrottle } = STUDIO_DEVIATIONS_STREAM;
  const { items: deviations, isFetching } = useNormalizedDeviationStream(
    streamId,
    streamThrottle,
    {
      ...streamConfig,
    }
  );
  const { selected, hasSelection, onSelect } = useDraftsSelect(deviations);
  const activeSection = useSelector(getActiveSection);
  const originalsData = useSelector(getOriginalsData);

  let ProductItem;
  let isUnlisted;
  let isSelectable = true;
  let isOriginals = false;

  switch (activeSection) {
    case StudioSection.PRODUCTS_COMMISSION:
      ProductItem = CommissionListItem;
      isSelectable = false;
      break;
    case StudioSection.PRODUCTS_ORIGINAL:
    case StudioSection.PRODUCTS_ORIGINAL_UNLISTED:
      ProductItem = OriginalListItem;
      isUnlisted = activeSection === StudioSection.PRODUCTS_ORIGINAL_UNLISTED;
      isSelectable = false;
      isOriginals = true;
      break;
    case StudioSection.PRODUCTS_EXCLUSIVE:
    case StudioSection.PRODUCTS_EXCLUSIVE_UNLISTED:
      ProductItem = ExclusiveListItem;
      isUnlisted = activeSection === StudioSection.PRODUCTS_EXCLUSIVE_UNLISTED;
      break;
    case StudioSection.PRODUCTS_PCP:
    case StudioSection.PRODUCTS_PCP_UNLISTED:
      ProductItem = PcpListItem;
      isUnlisted = activeSection === StudioSection.PRODUCTS_PCP_UNLISTED;
      break;
    case StudioSection.PRODUCTS_TIER:
    case StudioSection.PRODUCTS_TIER_UNLISTED:
      ProductItem = TierListItem;
      isUnlisted = activeSection === StudioSection.PRODUCTS_TIER_UNLISTED;
      isSelectable = false;
      break;
  }

  return (
    <ScrollableList
      beforeList={<FreeBoostsPromoBanner />}
      isFetching={isFetching}
      results={deviations}
      renderItem={(deviation, idx) => (
        <ProductItem
          key={deviation.deviationId}
          deviation={deviation}
          offset={idx}
          isSelectable={isSelectable}
          isUnlisted={isUnlisted}
          isSelected={selected.includes(idx)}
          isInSelectMode={hasSelection}
          onSelect={onSelect}
          {...(isOriginals && {
            originalsData: originalsData?.[deviation.deviationId],
          })}
        />
      )}
    />
  );
};
ProductList.displayName = 'ProductList';

export default ProductList;
